/**
 * Sprite units on screen (integration plan slice 3; the Rendering page's
 * sprite rule, on the path of the item billboards, `billboards.ts`): each unit
 * with a loaded sheet is a plane facing the camera with the camera's own
 * rotation, as the dungeon-floors prototype's Brad plane did (`dungeon-main.js`
 * `updateBrad`: `brad.quaternion.copy(camera.quaternion)`, so it leans with the
 * camera's pitch rather than standing on the world Y axis), turning about the
 * pivot on its tile. One `InstancedMesh` per sheet in use on the floor (a
 * sheet with no unit on the floor has no mesh, and its texture is freed), with
 * per-instance attributes: the frame's rectangle, the flip sign, the mix
 * (`shaders/sprite.ts`: a lerp toward a colour, for the gestures' flashes and
 * fades and the missing-faint dim), whether it casts a shadow, and the boss's
 * `palette.boss` tint in `instanceColor` (a multiply). Materials per sheet, as
 * the items have: unlit `MeshBasicMaterial` at Off; at Medium the lit
 * `MeshStandardMaterial` with the Medium light and fog hook, and a
 * `MeshDepthMaterial` as the `customDepthMaterial`, so the plane casts its own
 * cut-out shadow into the shadow map (the proxies' sheared cards in
 * `character-shadows.ts` are for proxies only). The Medium pair exists only at
 * Medium: going back to Off frees them (their programs too); the atlas
 * texture serves both tiers and stays. A unit faded past
 * `presentation.sprite.shadowFadeCut` casts no shadow; a `gone` one is not
 * drawn. Known limit (Rendering page): a camera-facing plane casts a thin
 * shadow under a side light; left as it is.
 *
 * The shadow map is static: `update` reports when what a shadow shows changed
 * (a sprite's position, drawn facing, flip, anim name, visibility or casting,
 * or the camera's rotation the planes take), never for a loop's frame steps
 * alone (an idle breath is not worth a shadow pass), so the view redraws it
 * only then. `staging` holds a sheet-less copy of this tier's materials, so
 * `precompile` compiles the sprite programs with the floor, before the first
 * sheet lands.
 */
import * as THREE from 'three';
import { presentation } from './constants.ts';
import { type ShaderHook, addShaderHook } from './material-hooks.ts';
import type { Palette } from './palette.ts';
import {
  spriteCastVertex,
  spriteCastVertexHeader,
  spriteMixFragmentHeader,
  spriteMixLit,
  spriteMixUnlit,
  spriteMixVertex,
  spriteMixVertexHeader,
  spriteUv,
  spriteVertexHeader,
} from './shaders/sprite.ts';
import { frameOf } from './sprite-anim.ts';
import type { SpriteSheet } from './sprites.ts';
import type { UnitPose } from './units.ts';

type MediumPair = { readonly lit: THREE.Material; readonly depth: THREE.Material };

type SheetMesh = {
  readonly mesh: THREE.InstancedMesh<THREE.BufferGeometry, THREE.Material>;
  readonly geometry: THREE.BufferGeometry;
  readonly frame: THREE.InstancedBufferAttribute;
  readonly flip: THREE.InstancedBufferAttribute;
  readonly mix: THREE.InstancedBufferAttribute;
  readonly cast: THREE.InstancedBufferAttribute;
  readonly texture: THREE.Texture;
  readonly off: THREE.Material;
  /** The Medium materials, only while the tier is Medium. */
  medium: MediumPair | null;
  readonly capacity: number;
  readonly texels: number;
};

type Plane = {
  readonly geometry: THREE.BufferGeometry;
  readonly frame: THREE.InstancedBufferAttribute;
  readonly flip: THREE.InstancedBufferAttribute;
  readonly mix: THREE.InstancedBufferAttribute;
  readonly cast: THREE.InstancedBufferAttribute;
};

const sp = presentation.sprite;
const uvHook: ShaderHook = { key: 'sprite-unit', vertexHeader: spriteVertexHeader, vertex: [{ chunk: 'uv_vertex', after: spriteUv }] };
const mixVertex = { vertexHeader: spriteMixVertexHeader, fragmentHeader: spriteMixFragmentHeader, vertex: [{ chunk: 'uv_vertex' as const, after: spriteMixVertex }] };
const unlitMix: ShaderHook = { key: 'sprite-mix', ...mixVertex, fragment: [{ chunk: 'color_fragment', after: spriteMixUnlit }] };
const litMix: ShaderHook = { key: 'sprite-mix-lit', ...mixVertex, fragment: [{ chunk: 'aomap_fragment', before: spriteMixLit }] };
const castHook: ShaderHook = { key: 'sprite-cast', vertexHeader: spriteCastVertexHeader, vertex: [{ chunk: 'project_vertex', after: spriteCastVertex }] };
const m = new THREE.Matrix4();
const local = new THREE.Matrix4();
const at = new THREE.Vector3();
const one = new THREE.Vector3(1, 1, 1);
const zero = new THREE.Matrix4().makeScale(0, 0, 0);
const tint = new THREE.Color();
const part = new THREE.Color();
const white = new THREE.Color(1, 1, 1);
const toward = new THREE.Color();

/** A new plane geometry (pivot at the bottom-left corner) with the per-instance attributes a sprite program reads. */
const planeWith = (capacity: number): Plane => {
  const geometry = new THREE.PlaneGeometry(1, 1).translate(0.5, 0.5, 0);
  const frame = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
  const flip = new THREE.InstancedBufferAttribute(new Float32Array(capacity).fill(1), 1);
  const mix = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
  const cast = new THREE.InstancedBufferAttribute(new Float32Array(capacity).fill(1), 1);
  geometry.setAttribute('abFrame', frame);
  geometry.setAttribute('abFlip', flip);
  geometry.setAttribute('abMix', mix);
  geometry.setAttribute('abCast', cast);
  return { geometry, frame, flip, mix, cast };
};

const offMaterial = (texture: THREE.Texture): THREE.Material => {
  const off = new THREE.MeshBasicMaterial({ map: texture, alphaTest: sp.alphaTest, side: THREE.DoubleSide });
  addShaderHook(off, uvHook);
  addShaderHook(off, unlitMix);
  return off;
};

const mediumMaterials = (texture: THREE.Texture, light: ShaderHook): MediumPair => {
  const lit = new THREE.MeshStandardMaterial({ map: texture, alphaTest: sp.alphaTest, side: THREE.DoubleSide, roughness: 1, metalness: 0 });
  addShaderHook(lit, uvHook);
  addShaderHook(lit, light);
  addShaderHook(lit, litMix);
  const depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, map: texture, alphaTest: sp.alphaTest, side: THREE.DoubleSide });
  addShaderHook(depth, uvHook);
  addShaderHook(depth, castHook);
  return { lit, depth };
};

/** The colour an instance lerps toward and by how much: the flash, then the missing-faint dim, then the fade, in the order a proxy's colour takes them. */
const mixOf = (p: UnitPose, sheet: SpriteSheet, palette: Palette): readonly [number, number, number, number] => {
  // Successive lerps toward several colours are one lerp: amount 1 - (1 - k1)(1 - k2)..., toward their weighted sum.
  let amount = 0;
  toward.setRGB(0, 0, 0);
  const into = (color: THREE.Color, k: number): void => {
    if (k <= 0) return;
    toward.multiplyScalar(1 - k).add(part.copy(color).multiplyScalar(k));
    amount = amount * (1 - k) + k;
  };
  if (p.flash) into(p.flash, p.flashAmount);
  if (p.down && !sheet.has('faint')) into(palette.background, sp.missingFaintFade);
  into(palette.background, p.fade);
  return amount > 0 ? [toward.r / amount, toward.g / amount, toward.b / amount, amount] : [0, 0, 0, 0];
};

export type SpriteDraw = { readonly pose: UnitPose; readonly sheet: SpriteSheet; readonly shown: boolean; readonly elevation: number };

type Staged = { readonly colour: THREE.Scene; readonly depth: THREE.Scene | null; dispose(): void };

export class SpriteLayer {
  private readonly meshes = new Map<string, SheetMesh>();
  private medium = false;
  /** Whether sprites cast into the shadow map at Medium (`setCasting`). */
  private casting = true;
  private signature: (number | string)[] = [];
  /** A sheet-less mesh with this tier's sprite materials, for `precompile` (never drawn; nothing of it is uploaded). */
  private staged: Staged | null = null;

  constructor(
    private readonly scene: THREE.Scene,
    private light: ShaderHook,
  ) {}

  /** A new light hook after a lost context: every mesh is made again on the next update. */
  rebind(light: ShaderHook): void {
    this.dispose();
    this.light = light;
  }

  /** The dungeon's sprites cast cut-out shadows into the map at Medium; a scene's do not (it draws projected shadows; slice 6a). */
  setCasting(casts: boolean): void {
    if (casts === this.casting) return;
    this.casting = casts;
    for (const s of this.meshes.values()) this.applyTier(s);
  }

  setTier(medium: boolean): void {
    if (medium === this.medium) return;
    this.medium = medium;
    this.unstage();
    for (const s of this.meshes.values()) this.applyTier(s);
  }

  /** The atlas texture a sheet in use draws with (its projected shadow samples it; slice 6a), or null. */
  textureOf(sheetId: string): THREE.Texture | null {
    return this.meshes.get(sheetId)?.texture ?? null;
  }

  /** Texels of the atlases on the GPU now (the sheets in use). */
  atlasTexels(): number {
    let n = 0;
    for (const s of this.meshes.values()) n += s.texels;
    return n;
  }

  /**
   * This tier's sprite programs on a staging mesh: `colour` compiles the Off or the lit program, `depth` (Medium)
   * the shadow pass's. Made on first use and kept until the tier changes or the layer is disposed.
   */
  staging(): { readonly colour: THREE.Scene; readonly depth: THREE.Scene | null } {
    if (this.staged !== null) return this.staged;
    const texture = new THREE.DataTexture(new Uint8Array(4), 1, 1);
    texture.colorSpace = THREE.SRGBColorSpace;
    const { geometry } = planeWith(1);
    const pair = this.medium ? mediumMaterials(texture, this.light) : null;
    const materials: readonly THREE.Material[] = pair === null ? [offMaterial(texture)] : [pair.lit, pair.depth];
    const sceneOf = (material: THREE.Material): THREE.Scene => {
      const mesh = new THREE.InstancedMesh(geometry, material, 1);
      mesh.setColorAt(0, white);
      mesh.frustumCulled = false;
      return new THREE.Scene().add(mesh);
    };
    const [colour, depth] = materials.map(sceneOf);
    if (colour === undefined) throw new Error('no sprite staging material');
    const staged: Staged = {
      colour,
      depth: depth ?? null,
      dispose() {
        for (const material of materials) material.dispose();
        geometry.dispose();
        texture.dispose();
      },
    };
    this.staged = staged;
    return staged;
  }

  /**
   * Places every sprite unit for this frame at `clock` (the unit layer's anim clock); `time` is the frame time
   * (the charge tremble); `scale` is world units per tile (a scene's; 1 in the dungeon). Returns whether anything a
   * shadow shows changed since the last frame.
   */
  update(draws: readonly SpriteDraw[], camera: THREE.Camera, palette: Palette, time: number, clock: number, scale = 1): boolean {
    const bySheet = new Map<string, SpriteDraw[]>();
    for (const d of draws) {
      const list = bySheet.get(d.sheet.id);
      if (list === undefined) bySheet.set(d.sheet.id, [d]);
      else list.push(d);
    }
    for (const [id, s] of [...this.meshes]) {
      if (!bySheet.has(id)) {
        this.free(s);
        this.meshes.delete(id);
      }
    }
    const signature: (number | string)[] = [camera.quaternion.x, camera.quaternion.y, camera.quaternion.z, camera.quaternion.w];
    const g = presentation.gesture;
    for (const [id, list] of bySheet) {
      const first = list[0];
      if (first === undefined) continue;
      const s = this.ensure(id, first.sheet, list.length);
      list.forEach((d, i) => {
        const { pose: p, sheet } = d;
        signature.push(p.id);
        if (!d.shown) {
          s.mesh.setMatrixAt(i, zero);
          signature.push(-1);
          return;
        }
        const { resolved, index } = frameOf(p, sheet, clock);
        const frame = resolved.run.frames[index] ?? 0;
        at.set(p.x + p.lungeX + p.swayX, d.elevation + p.lift + (p.charging ? g.chargeCrouch : 0), -(p.y + p.lungeY + p.swayY));
        const casts = p.fade < sp.shadowFadeCut;
        // The frame index and a held charge's tremble are a loop's steps: they move the plane, never the shadow's trigger.
        signature.push(resolved.name, resolved.drawn, resolved.flip ? 1 : 0, at.x, at.y, at.z, casts ? 1 : 0);
        if (p.charging) at.x += Math.sin(time / g.trembleMs) * g.tremble;
        const pivotX = (resolved.flip ? sheet.width - sheet.pivotX : sheet.pivotX) * scale;
        m.compose(at, camera.quaternion, one).multiply(local.makeTranslation(-pivotX, -sheet.pivotY * scale, 0)).multiply(local.makeScale(sheet.width * scale, sheet.height * scale, 1));
        s.mesh.setMatrixAt(i, m);
        s.frame.setXYZW(i, sheet.rects[frame * 4] ?? 0, sheet.rects[frame * 4 + 1] ?? 0, sheet.rects[frame * 4 + 2] ?? 0, sheet.rects[frame * 4 + 3] ?? 0);
        s.flip.setX(i, resolved.flip ? -1 : 1);
        const [r, gr, b, amount] = mixOf(p, sheet, palette);
        s.mix.setXYZW(i, r, gr, b, amount);
        s.cast.setX(i, casts ? 1 : 0);
        s.mesh.setColorAt(i, p.boss ? tint.copy(white).lerp(palette.boss, sp.bossTint) : white);
      });
      s.mesh.count = list.length;
      s.mesh.instanceMatrix.needsUpdate = true;
      if (s.mesh.instanceColor) s.mesh.instanceColor.needsUpdate = true;
      for (const attribute of [s.frame, s.flip, s.mix, s.cast]) attribute.needsUpdate = true;
    }
    const changed = signature.length !== this.signature.length || signature.some((v, i) => v !== this.signature[i]);
    this.signature = signature;
    return changed;
  }

  dispose(): void {
    for (const s of this.meshes.values()) this.free(s);
    this.meshes.clear();
    this.signature = [];
    this.unstage();
  }

  private unstage(): void {
    this.staged?.dispose();
    this.staged = null;
  }

  /** Off draws the unlit material and frees the Medium pair; Medium makes the lit and depth pair and casts and receives shadows. */
  private applyTier(s: SheetMesh): void {
    if (this.medium) {
      s.medium ??= mediumMaterials(s.texture, this.light);
      s.mesh.material = s.medium.lit;
      s.mesh.customDepthMaterial = this.casting ? s.medium.depth : undefined;
    } else {
      s.mesh.material = s.off;
      s.mesh.customDepthMaterial = undefined;
      s.medium?.lit.dispose();
      s.medium?.depth.dispose();
      s.medium = null;
    }
    s.mesh.castShadow = this.medium && this.casting;
    s.mesh.receiveShadow = this.medium;
  }

  /** The sheet's mesh, made (with its texture and materials) when the sheet first has a unit, and remade larger when it outgrows it. */
  private ensure(id: string, sheet: SpriteSheet, count: number): SheetMesh {
    const known = this.meshes.get(id);
    if (known !== undefined && count <= known.capacity) return known;
    if (known !== undefined) this.free(known);
    const capacity = Math.max(sp.initialCapacity, count * 2);
    const { geometry, frame, flip, mix, cast } = planeWith(capacity);
    const texture = sheet.texture();
    const off = offMaterial(texture);
    const mesh = new THREE.InstancedMesh<THREE.BufferGeometry, THREE.Material>(geometry, off, capacity);
    mesh.name = `sprite-${id}`;
    mesh.frustumCulled = false;
    mesh.setColorAt(0, white);
    this.scene.add(mesh);
    const made: SheetMesh = { mesh, geometry, frame, flip, mix, cast, texture, off, medium: null, capacity, texels: sheet.atlas.width * sheet.atlas.height };
    this.applyTier(made);
    this.meshes.set(id, made);
    return made;
  }

  private free(s: SheetMesh): void {
    s.mesh.removeFromParent();
    s.mesh.dispose();
    s.geometry.dispose();
    s.texture.dispose();
    for (const material of [s.off, s.medium?.lit, s.medium?.depth]) material?.dispose();
  }
}
